import Bold from "@tiptap/extension-bold";
import Document from "@tiptap/extension-document";
import FontSize from "@tiptap/extension-font-size";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { Color, TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { useEditor } from "@tiptap/react";
import { useEffect, useRef } from "react";
import { useUnavailableVariableIds } from "@babackapp/variables-registry";
import createVariablesMentionExtension from "../../lib/mention-extension";
import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";
import useThemeContextualColors from "../../hooks/use-theme-contextual-colors";
import { sectionToParent } from "../../hooks";
import { tLayout } from "../../translations";
import { isEmptyHtml, type InstructionKey } from "../return-shipping-instructions/instructions";
import { getTitleDefault, TITLE_FONT_SIZE_PX } from "./title-defaults";
import type { ReturnShippingInstructionsTitleBlock } from "./types";

/**
 * Rich-text editor for a single title override, mirroring the
 * `return-shipping-instructions` block's `useInstructionEditor` but writing to
 * the title block's `content[locale][instructionKey]` and using the title
 * default (bold, 18px) as the placeholder.
 *
 * Intended to be remounted (via `key={instructionKey}`) when the selected
 * title changes, so the editor re-initializes with the right content.
 */
export function useTitleEditor(instructionKey: InstructionKey) {
  const blockProps = useBlockPanelPropsContext();
  const { formAPI, locale, defaultLocale, section, theme } = blockProps;
  const block = blockProps.block as ReturnShippingInstructionsTitleBlock;
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const unavailableVariableIds = useUnavailableVariableIds();

  // Only read THIS locale's own override — never the defaultLocale's. Falling
  // back to defaultLocale here would pre-fill the editor with another locale's
  // text and, on the next save, persist it under the current locale (mislabeling
  // e.g. English copy as a French translation). An empty locale shows the
  // default title as a placeholder instead. The renderer keeps the defaultLocale
  // fallback for display; the editor must not.
  const content = block.content?.[locale]?.[instructionKey] ?? "";

  // Default title for this key (shown as a greyed placeholder when empty),
  // resolved like the renderer: tLayout with a hardcoded English fallback.
  const t = tLayout(locale, defaultLocale);
  const def = getTitleDefault(instructionKey);
  const translated = t(def.tLayoutKey);
  const placeholder = translated === def.tLayoutKey ? def.fallback : translated;

  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const blockRef = useRef(block);
  const formAPIRef = useRef(formAPI);
  blockRef.current = block;
  formAPIRef.current = formAPI;

  const editor = useEditor({
    extensions: [
      Document,
      createVariablesMentionExtension(
        blockProps.variables,
        unavailableVariableIds,
      ),
      Paragraph.configure({
        HTMLAttributes: {
          // Default size matches the rendered title (TITLE_FONT_SIZE_PX) but
          // weight is left "normal" so the Bold toolbar button actually toggles
          // `<strong>` — baking in `font-weight:bold` here would make the text
          // permanently bold and the toggle a no-op.
          style: `font-size:${TITLE_FONT_SIZE_PX}px;font-weight:normal;margin:0;padding:0;`,
        },
      }),
      Text,
      Bold,
      Italic,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "underline text-foreground",
        },
      }),
      TextStyle,
      Color,
      FontSize.configure({
        types: ["textStyle"],
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: content || "",
    onUpdate: ({ editor }) => {
      const raw = editor.getHTML();
      // Store "" instead of the editor's "<p></p>" when cleared, so the renderer
      // falls back to the default title and no empty override lingers.
      const newContent = isEmptyHtml(raw) ? "" : raw;

      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      updateTimeoutRef.current = setTimeout(() => {
        const current = blockRef.current;
        const updatedBlock = {
          ...current,
          content: {
            ...current.content,
            [locale]: {
              ...current.content?.[locale],
              [instructionKey]: newContent,
            },
          },
        };
        formAPIRef.current.updateField(
          formAPIRef.current.blockPath,
          updatedBlock,
        );
      }, 300);
    },
    editorProps: {
      attributes: {
        class:
          "min-h-[120px] max-h-[400px] overflow-auto px-3 py-2 text-sm rounded-md border bg-background outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:ring-1 focus-visible:ring-ring",
        style: `background-color: ${colors.bg}; color: ${colors.text}`,
      },
    },
  });

  useEffect(() => {
    if (editor && content) {
      const currentContent = editor.getHTML();
      if (currentContent !== content) {
        editor.commands.setContent(content, {
          emitUpdate: false,
        });
      }
    }
  }, [editor, content, locale, instructionKey]);

  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return editor;
}
